import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { collect } from 'collect.js';
import { Button, Flex, Steps } from 'antd';
import SubscriptionWithTermStep from '../verification/SubscriptionWithTermStep';
import { Mode, SubscriptionWrapper } from '../verification/SubscriptionStep';
import Subscription from '../billing/Subscription';
import YousignBusinessInfoStep, { contractFields } from './yousign/YousignBusinessInfoStep';
import YousignCreatePreviewStep from './yousign/YousignCreatePreviewStep';
import YousignPreviewStep from './yousign/YousignPreviewStep';
import YousignSignContractStep from './yousign/YousignSignContractStep';
import LoadingScreen from '../loading/LoadingScreen';
import useForm from '../../hooks/utility/useForm';
import useApiClient from '../../hooks/api/useApiClient';
import useFeedbackLabel from '../../hooks/ui/useFeedbackLabel';
import {
  useGetSubscriptionsQuery,
  usePartialUpdateServiceContractMutation,
  usePartialUpdateSubscriptionMutation,
  useSwitchToAnnualMutation,
} from '../../store/api-slices';
import { clampDiscount, discountSubscription } from '../../services/helpers/ContractDiscountHelpers';
import {
  ContractFlow,
  SignatureProvider,
  SubscriptionProductType,
  SubscriptionTerm,
} from '../../services/exports/Constants';

/**
 * Ordered keys for the switch-to-annual wizard.
 */
export const AnnualStepKey = Object.freeze({
  Discount: 'discount',
  MobileApp: 'mobile_app',
  BusinessInfo: 'business_info',
  CreatePreview: 'create_preview',
  Preview: 'preview',
  Sign: 'sign',
});

const STEP_ORDER = [
  AnnualStepKey.Discount,
  AnnualStepKey.MobileApp,
  AnnualStepKey.BusinessInfo,
  AnnualStepKey.CreatePreview,
  AnnualStepKey.Preview,
  AnnualStepKey.Sign,
];

// Contract business fields collected in this flow. The bank-account fields are
// omitted: this flow renders the business-info step with `hasSetupFee={false}`,
// so those inputs are never shown here.
const FLOW_CONTRACT_FIELDS = contractFields.filter(
  (field) => !['business_bank_account_holder_name', 'business_bank_account_number'].includes(field),
);

// Contract business fields that must be filled before the business-info step
// counts as complete — every collected field except the optional tax id.
const REQUIRED_BUSINESS_FIELDS = FLOW_CONTRACT_FIELDS.filter((field) => field !== 'business_tax_id');

/**
 * Merge a contract's staged `pending_changes` over its committed values so the
 * form reads pending-first, original-fallback. The nested `pending_changes` key
 * is dropped from the result.
 */
const mergePendingChanges = (data) => {
  if (!data) {
    return data;
  }

  const { pending_changes, ...rest } = data;

  return { ...rest, ...(pending_changes ?? {}), contract_term: SubscriptionTerm.Annual };
};

/**
 * Reduce a basket line item to the staged-item payload the backend expects:
 * the full undiscounted price definition is sent as `price_data` (not a
 * `price_id` reference to the subscription's current price), so the backend
 * recomputes the annual discount from the original list price and tiers.
 */
const toStagedSubscriptionItem = ({ price, ...item }) => ({
  ...item,
  price_data: price,
});

/**
 * Switch-to-annual wizard. An already-signed monthly contract is amended to an
 * annual commitment: each step stages only its own fields into the contract's
 * `pending_changes` via partialUpdate (nothing is committed to the live
 * contract), the amended contract is previewed and re-signed through Yousign,
 * and the staged changes are applied on sign via the switch-to-annual mutation.
 */
export default function SwitchToAnnualFlow({ contract, onSuccess }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Contract:SwitchToAnnualFlow' });
  const { t: tBusiness } = useTranslation(undefined, { keyPrefix: 'Components:Contract:BusinessInfo' });
  const { t: tSign } = useTranslation(undefined, { keyPrefix: 'Components:Contract:SignStep' });

  // Backend validation errors staged by this wizard can name a business field or
  // a recipient field, whose labels live in two different namespaces. Resolve
  // against business info first, then fall back to the sign step (which also
  // supplies the raw-key default). `FeedbackLabel` calls this as
  // `t('form.<field>.label', { defaultValue: '<field>' })`.
  const translateField = useCallback(
    (key, options) => tBusiness(key, { ...options, defaultValue: '' }) || tSign(key, options),
    [tBusiness, tSign],
  );
  const { setFeedback, setErrorFeedback, resetFeedback, renderFeedbackLabel } = useFeedbackLabel({
    t: translateField,
  });

  // The discount step stages the contract and the subscription concurrently, so
  // a plain `setErrorFeedback` per stage would be last-write-wins and hide one
  // side's fields. Merge instead, and let both stages share a single toast.
  const addErrorFeedback = useCallback(
    (response) =>
      setFeedback((previous) => ({
        type: 'error',
        message: previous?.message || response?.message,
        errors: { ...(previous?.errors ?? {}), ...(response?.errors ?? {}) },
      })),
    [setFeedback],
  );

  const toastSaveFailed = () =>
    // One toast per staging attempt, however many stages failed.
    toast.error(t('toasts.failed_to_save_changes'), { toastId: 'switch-to-annual-save-failed' });

  const { ServiceContractsManager, SubscriptionsManager } = useApiClient();
  const [partialUpdate] = usePartialUpdateServiceContractMutation();
  const [partialUpdateSubscription] = usePartialUpdateSubscriptionMutation();
  const [switchToAnnual] = useSwitchToAnnualMutation();

  const contractId = contract?.id;

  // Standalone mobile-app subscription (single mobile-app-plan item, separate
  // from the master subscription). Drives the optional mobile-app step: shown
  // read-only there so the merchant sees the plan the annual discount will also
  // apply to. Shares the RTK Query cache with the discount step's
  // SubscriptionWrapper (same query args after apiManager is stripped).
  const { company } = useSelector((state) => state.currentCompany);
  const { data: subscriptionsResponse } = useGetSubscriptionsQuery({
    companyId: company?.id,
    apiManager: SubscriptionsManager,
  });
  const mobileAppSubscription = useMemo(
    () =>
      (subscriptionsResponse?.data ?? []).find(
        (subscription) =>
          subscription.items.length === 1 &&
          !!collect(subscription.items).first((item) =>
            [SubscriptionProductType.MobileAppPlan].includes(item.product.type),
          ),
      ) ?? null,
    [subscriptionsResponse],
  );

  // Pending-first snapshot used to seed the steps; refreshed after each staged
  // partialUpdate so navigating back/forward keeps the staged values.
  const [localContract, setLocalContract] = useState(() => mergePendingChanges(contract));
  const [currentStepKey, setCurrentStepKey] = useState(AnnualStepKey.Discount);
  const [signatureLink, setSignatureLink] = useState(null);
  const [initializing, setInitializing] = useState(true);
  // Shared loading flag while a step stages its fields via `persist`. Owned here
  // (rather than per-step) since `persist` lives here and only one step renders
  // at a time; passed down to the active step as `isPersisting`.
  const [persisting, setPersisting] = useState(false);

  // Recipient/create-preview fields — one shared in-memory form, seeded
  // pending-first from the contract snapshot.
  const { data, update } = useForm({
    signer_first_name: localContract?.signer_first_name ?? '',
    signer_last_name: localContract?.signer_last_name ?? '',
    recipient_email: localContract?.recipient_email ?? '',
    additional_locations: localContract?.additional_locations ?? [],
    special_agreements: localContract?.special_agreements ?? '',
  });

  // Business fields live directly on `localContract` (the always-fresh
  // pending-first snapshot) rather than the seeded-once recipient form, so the
  // business-info step reflects values staged by earlier steps.
  const updateBusinessField = (field, value) => setLocalContract((prev) => ({ ...prev, [field]: value }));

  // Resume from persisted signals only: an active switch-to-annual signature
  // request → sign step; otherwise start from the first (discount) step.
  useEffect(() => {
    if (!contract?.id) {
      return;
    }

    let cancelled = false;

    ServiceContractsManager.getSignatureLink(contract.id, { contract_flow: ContractFlow.SwitchToAnnual })
      .then((response) => {
        if (cancelled) {
          return;
        }

        if (response?.success && response.data) {
          setSignatureLink(response.data);
          setCurrentStepKey(AnnualStepKey.Sign);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setInitializing(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [contract?.id]);

  // The mobile-app step only exists when the company has a standalone
  // mobile-app subscription to show.
  const steps = useMemo(
    () =>
      STEP_ORDER.filter((key) => key !== AnnualStepKey.MobileApp || !!mobileAppSubscription).map((key) => ({
        key,
        title: t(`steps.${key}`),
      })),
    [t, mobileAppSubscription],
  );

  const currentStepIndex = Math.max(
    0,
    steps.findIndex((step) => step.key === currentStepKey),
  );

  const goTo = (key) => {
    // A validation alert belongs to the step that produced it — never carry it over.
    resetFeedback();
    setCurrentStepKey(key);
  };

  // Neighbours in the *rendered* step list, so navigation skips the optional
  // mobile-app step when it's absent.
  const stepAfter = (key) => steps[steps.findIndex((step) => step.key === key) + 1]?.key;
  const stepBefore = (key) => steps[steps.findIndex((step) => step.key === key) - 1]?.key;

  // Run a staging operation behind the shared loading flag. Owned here (rather
  // than per-call) so concurrent stages share a single flag and it only clears
  // once every one has settled.
  const withPersisting = async (work) => {
    resetFeedback();
    setPersisting(true);

    try {
      return await work();
    } finally {
      setPersisting(false);
    }
  };

  // Stage a single step's fields into the contract's `pending_changes`. Returns
  // true on success. Does not own the loading flag — wrap with `withPersisting`.
  const stageContract = async (payload) => {
    const { data: updated, error } = await partialUpdate({
      apiManager: ServiceContractsManager,
      id: contractId,
      data: payload,
    });

    if (error) {
      // `baseQuery` passes the raw backend body through as `error`, so this is
      // the `{ message, errors }` validation payload.
      addErrorFeedback(error);
      toastSaveFailed();

      return false;
    }

    if (updated) {
      setLocalContract(mergePendingChanges(updated));
    }

    return true;
  };

  // Stage the master subscription's fields (billing mode, trial, line items)
  // into its own `pending_changes`, mirroring how contract fields are staged.
  // Returns true on success. Does not own the loading flag.
  const stageSubscription = async (id, payload) => {
    const { error } = await partialUpdateSubscription({
      apiManager: SubscriptionsManager,
      id,
      data: payload,
    });

    if (error) {
      addErrorFeedback(error);
      toastSaveFailed();

      return false;
    }

    return true;
  };

  // Stage a single step's fields into `pending_changes`. Returns true on success.
  const persist = (payload) => withPersisting(() => stageContract(payload));

  // Kick off signing via switch-to-annual instead of the plain init-sign: the
  // endpoint applies the staged `pending_changes` and returns the signature link.
  // Normalized to the `{ success, data }` shape `YousignPreviewStep` expects.
  // `YousignPreviewStep` only reports pass/fail, so the backend's validation
  // errors are surfaced here — the wizard's own alert sits above the active step.
  const initSign = async (id, payload) => {
    resetFeedback();

    const { data: link, error } = await switchToAnnual({
      apiManager: ServiceContractsManager,
      id,
      data: payload,
    });

    if (error) {
      setErrorFeedback(error);

      return { success: false, data: null };
    }

    return { success: true, data: link };
  };

  // Signing succeeded — apply the staged changes to the contract.
  const onSigned = async (signedContract) => {
    toast.success(t('toasts.switched_to_annual'));

    return onSuccess?.(signedContract);
  };

  // A step's own completion requirement. The discount step needs a subscription
  // with the annual commitment to have been selected/staged (the whole point of
  // this wizard); the business-info step needs every required business field
  // filled; other steps have no extra gate here.
  const isStepValid = (stepKey) => {
    switch (stepKey) {
      case AnnualStepKey.Discount:
        return localContract?.contract_term === SubscriptionTerm.Annual;
      case AnnualStepKey.BusinessInfo:
        return REQUIRED_BUSINESS_FIELDS.every((field) => String(localContract?.[field] ?? '').trim() !== '');
      default:
        return true;
    }
  };

  // A step is only clickable once every preceding step is valid, so you can't
  // skip ahead past an incomplete step. The sign step additionally requires an
  // active signature request.
  const isStepClickable = (step, index) => {
    if (step.key === AnnualStepKey.Sign && !signatureLink) {
      return false;
    }

    return steps.slice(0, index).every((prior) => isStepValid(prior.key));
  };

  const onStepChange = (index) => {
    const target = steps[index];

    if (!target || !isStepClickable(target, index)) {
      return;
    }

    goTo(target.key);
  };

  function renderStep() {
    switch (currentStepKey) {
      case AnnualStepKey.Discount:
        return (
          <SubscriptionWrapper
            renderSubscriptions={({ loading, subscriptions, subscriptionProducts }) => {
              if (loading) {
                return <LoadingScreen />;
              }

              const masterSubscriptionIndex = collect(subscriptions).search(
                (subscription) =>
                  !!collect(subscription.items).first((item) =>
                    [SubscriptionProductType.Plan, SubscriptionProductType.LegacyPlan].includes(item.product.type),
                  ),
              );
              const masterSubscription =
                masterSubscriptionIndex !== false ? mergePendingChanges(subscriptions[masterSubscriptionIndex]) : null;
              const hasMobileAppItem = !!collect(masterSubscription.items).first(
                (item) => item.product.type === SubscriptionProductType.MobileAppPlan,
              );

              return (
                <>
                  <SubscriptionWithTermStep
                    key="switch-to-annual-discount"
                    providedServiceContract={localContract}
                    data={masterSubscription}
                    subscriptionProducts={subscriptionProducts}
                    mode={Mode.edit}
                    filterOtherProducts={(item) =>
                      ![
                        SubscriptionProductType.GoogleAdsPlan,
                        ...(hasMobileAppItem ? [] : [SubscriptionProductType.MobileAppPlan]),
                      ].includes(item.type)
                    }
                    readOnlyProducts={[SubscriptionProductType.MobileAppPlan]}
                    onPersist={async ({ contract_term, contract_discount_percentage, items, ...subscriptionData }) => {
                      // Stage the contract fields and the subscription fields
                      // concurrently under one loading flag.
                      const results = await withPersisting(() =>
                        Promise.all([
                          stageContract({ contract_term, contract_discount_percentage }),
                          masterSubscription?.id
                            ? stageSubscription(masterSubscription.id, {
                                ...subscriptionData,
                                items: (items ?? []).map(toStagedSubscriptionItem),
                              })
                            : Promise.resolve(true),
                        ]),
                      );

                      if (!results.every(Boolean)) {
                        return;
                      }

                      goTo(stepAfter(AnnualStepKey.Discount));
                    }}
                    isPersisting={persisting}
                    hasTermSelector={false}
                    renderAgreementText={({ discount, planName, amount }) =>
                      t('agreement', { discount, plan: planName, amount, months: 12 })
                    }
                  />
                </>
              );
            }}
          />
        );
      case AnnualStepKey.MobileApp: {
        if (!mobileAppSubscription) {
          return null;
        }

        // Read-only preview of the standalone mobile-app subscription with the
        // staged annual discount applied for display (the backend recomputes it
        // on switch); it can't be cancelled or edited from this wizard.
        const discountedMobileApp = discountSubscription(
          mobileAppSubscription,
          localContract?.contract_discount_percentage ? clampDiscount(localContract.contract_discount_percentage) : 0,
        );

        return (
          <div>
            <Subscription data={discountedMobileApp} cancellable={false} />
            <Flex justify="space-between" gap="small" className="mt-lg">
              <Button onClick={() => goTo(AnnualStepKey.Discount)}>{t('buttons.back')}</Button>
              <Button type="primary" onClick={() => goTo(AnnualStepKey.BusinessInfo)} className="ml-auto">
                {t('buttons.continue')}
              </Button>
            </Flex>
          </div>
        );
      }

      case AnnualStepKey.BusinessInfo:
        return (
          <YousignBusinessInfoStep
            contract={localContract}
            hasSetupFee={false}
            setContract={setLocalContract}
            data={localContract}
            update={updateBusinessField}
            onPersist={() =>
              persist(Object.fromEntries(FLOW_CONTRACT_FIELDS.map((field) => [field, localContract?.[field]])))
            }
            isPersisting={persisting}
            disabled={!isStepValid(AnnualStepKey.BusinessInfo)}
            onBack={() => goTo(stepBefore(AnnualStepKey.BusinessInfo))}
            onNext={() => goTo(AnnualStepKey.CreatePreview)}
          />
        );
      case AnnualStepKey.CreatePreview:
        return (
          <YousignCreatePreviewStep
            data={data}
            update={update}
            onPersist={() =>
              persist({
                signer_first_name: data.signer_first_name,
                signer_last_name: data.signer_last_name,
                recipient_email: data.recipient_email,
                additional_locations: data.additional_locations,
                special_agreements: data.special_agreements,
                signature_provider: SignatureProvider.Yousign,
              }).then((ok) => ok && goTo(AnnualStepKey.Preview))
            }
            isPersisting={persisting}
            onBack={() => goTo(AnnualStepKey.BusinessInfo)}
            onNext={() => goTo(AnnualStepKey.Preview)}
          />
        );
      case AnnualStepKey.Preview:
        return (
          <YousignPreviewStep
            contract={localContract}
            data={{
              signature_provider: SignatureProvider.Yousign,
              hide_setup_fees: true,
              with_pending_changes: true,
            }}
            initSign={initSign}
            onBack={() => goTo(AnnualStepKey.CreatePreview)}
            onCreated={(link) => {
              setSignatureLink(link);
              goTo(AnnualStepKey.Sign);
            }}
          />
        );
      case AnnualStepKey.Sign:
        return (
          <YousignSignContractStep
            contractId={contract?.id}
            signatureLink={signatureLink}
            onSuccess={onSigned}
            onCancel={() => {
              setSignatureLink(null);
              goTo(AnnualStepKey.Preview);
            }}
          />
        );
      default:
        return null;
    }
  }

  if (initializing) {
    return <LoadingScreen />;
  }

  return (
    <div>
      <Steps
        current={currentStepIndex}
        onChange={onStepChange}
        items={steps.map((step, index) => ({
          title: step.title,
          disabled: !isStepClickable(step, index),
        }))}
        className="mb-lg"
        responsive
      />
      {renderFeedbackLabel}
      {renderStep()}
    </div>
  );
}
